import type { AppValueFormat } from "./catalog";
import { EMPTY_VALUE, formatAppValue, isNumericFormat, toDate } from "./format";

/**
 * Sorting and filtering for the Apps Table, kept apart from the React
 * component so the rules are testable without rendering.
 *
 * Filters work on what the user sees: a filter matches a cell's formatted text,
 * so the values offered in a column's menu read exactly like the cells. Sorting
 * works on the underlying value, read the way the column's format reads it.
 */

export type AppTableRow = Record<string, unknown>;

export interface AppTableColumn {
  key: string;
  format?: AppValueFormat;
  currency?: string;
  sortable?: boolean;
  filterable?: boolean;
}

export type AppTableSort = { key: string; direction: "asc" | "desc" };

/**
 * Column key → the formatted values the user kept. A missing key keeps every
 * row. A Map, so a column keyed `constructor` is not an inherited property.
 */
export type AppTableFilters = ReadonlyMap<string, ReadonlySet<string>>;

/** A column offers value filters only when it has few enough distinct values to list. */
export const MAX_FILTER_VALUES = 25;

export function canSortColumn(column: AppTableColumn): boolean {
  return column.sortable ?? true;
}

/** Numbers are filtered by sorting, not by picking values, unless the author opts in. */
export function canFilterColumn(column: AppTableColumn): boolean {
  return column.filterable ?? !isNumericFormat(column.format);
}

/**
 * What a column's header menu offers. Filtering needs at least two values to
 * choose between, so a column of one repeated value only sorts — unless a
 * filter is already active, which must stay reachable to be cleared even when
 * the rows changed under it.
 */
export function columnMenu(
  column: AppTableColumn,
  values: readonly string[],
  filtered = false,
): { sortable: boolean; filterable: boolean } {
  return {
    sortable: canSortColumn(column),
    filterable: canFilterColumn(column) && (filtered || values.length > 1),
  };
}

export function cellText(row: AppTableRow, column: AppTableColumn): string {
  return formatAppValue(row[column.key], column.format, {
    currency: column.currency,
  });
}

function isEmpty(value: unknown): boolean {
  return value === null || value === undefined || value === "";
}

/**
 * The value a sort compares, read the way the column displays it: numeric
 * columns accept numeric strings ("1.10"), date columns compare instants (so
 * ISO stamps with different offsets order chronologically, and epoch millis
 * read as the formatter reads them). Anything else sorts by its display text,
 * which never throws — cells are arbitrary JSON.
 */
function sortKey(
  value: unknown,
  format: AppValueFormat | undefined,
): number | string {
  if (isNumericFormat(format)) {
    const number =
      typeof value === "number"
        ? value
        : typeof value === "string"
          ? Number(value)
          : Number.NaN;
    if (Number.isFinite(number)) return number;
  }
  if (format === "date" || format === "datetime") {
    const parsed = toDate(value);
    if (parsed) return parsed.date.getTime();
  }
  if (typeof value === "number") return value;
  return formatAppValue(value);
}

/**
 * A total order over sort keys: numbers compare numerically, text compares as
 * text, and every number sorts before every text value. Comparing a mixed pair
 * as text instead would not be transitive (-10 < -2 < "-5" < -10), and turning
 * a whole column to text would put one bad date's neighbours out of order.
 */
function compareValues(a: number | string, b: number | string): number {
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "number") return -1;
  if (typeof b === "number") return 1;
  return a.localeCompare(b, undefined, { numeric: true });
}

/** A stable sort; empty cells always sink to the bottom, whichever the direction. */
export function sortRows(
  rows: readonly AppTableRow[],
  sort: AppTableSort | undefined,
  /** The table's current columns; omit only when there is no column metadata. */
  columns?: readonly AppTableColumn[],
): AppTableRow[] {
  if (!sort) return [...rows];
  const column = columns?.find((c) => c.key === sort.key);
  // Like a stale filter: a sort on a column that is gone or can no longer
  // sort has no menu left to clear it, so it must stop reordering rows.
  if (columns && (!column || !canSortColumn(column))) return [...rows];
  const sign = sort.direction === "asc" ? 1 : -1;
  const format = column?.format;
  // Each cell's key is read once, not per comparison: date parsing and
  // formatting objects are too costly to repeat O(n log n) times.
  const filled: { row: AppTableRow; key: number | string }[] = [];
  const empty: AppTableRow[] = [];
  for (const row of rows) {
    const value = row[sort.key];
    if (isEmpty(value)) empty.push(row);
    else filled.push({ row, key: sortKey(value, format) });
  }
  filled.sort((a, b) => sign * compareValues(a.key, b.key));
  return [...filled.map(({ row }) => row), ...empty];
}

export function filterRows(
  rows: readonly AppTableRow[],
  columns: readonly AppTableColumn[],
  filters: AppTableFilters,
): AppTableRow[] {
  // A stored filter only applies while its column can still be filtered; if
  // the author turns filtering off (or the format changes), its menu — and so
  // its Clear filter — is gone, so the filter must stop hiding rows too.
  const active = columns.filter(
    (column) => canFilterColumn(column) && filters.has(column.key),
  );
  if (active.length === 0) return [...rows];
  return rows.filter((row) =>
    active.every((column) =>
      filters.get(column.key)?.has(cellText(row, column)),
    ),
  );
}

/**
 * The distinct formatted values of a column, in first-seen order; empty past
 * the limit, and for a column that cannot filter (its menu offers none). Blank cells are one value of their own (`EMPTY_VALUE`), listed
 * last, so they can be kept or dropped like any other.
 */
export function filterValues(
  rows: readonly AppTableRow[],
  column: AppTableColumn,
): string[] {
  if (!canFilterColumn(column)) return [];
  const values = new Set<string>();
  let hasBlank = false;
  for (const row of rows) {
    const text = cellText(row, column);
    if (text === EMPTY_VALUE) hasBlank = true;
    else values.add(text);
    if (values.size + Number(hasBlank) > MAX_FILTER_VALUES) return [];
  }
  return hasBlank ? [...values, EMPTY_VALUE] : [...values];
}

/**
 * Toggle one value in a column's filter. Starts from every value kept, and
 * drops the column's filter once every value is kept again.
 */
export function toggleFilterValue(
  filters: AppTableFilters,
  key: string,
  value: string,
  allValues: readonly string[],
): AppTableFilters {
  const kept = new Set(filters.get(key) ?? allValues);
  if (kept.has(value)) kept.delete(value);
  else kept.add(value);
  const next = new Map(filters);
  if (allValues.every((v) => kept.has(v))) next.delete(key);
  else next.set(key, kept);
  return next;
}

export function clearFilter(
  filters: AppTableFilters,
  key: string,
): AppTableFilters {
  const next = new Map(filters);
  next.delete(key);
  return next;
}

/** The direction a column is sorted in, or false when it is not the sort key. */
export function sortDirection(
  sort: AppTableSort | undefined,
  key: string,
): AppTableSort["direction"] | false {
  return sort?.key === key ? sort.direction : false;
}

export function ariaSort(
  sort: AppTableSort | undefined,
  key: string,
): "ascending" | "descending" | undefined {
  const direction = sortDirection(sort, key);
  if (!direction) return undefined;
  return direction === "asc" ? "ascending" : "descending";
}

/**
 * A row's React key. A row's own `id` when it has one; otherwise its position
 * plus its displayed cells, so a row's ⋯ menu remounts — rather than silently
 * retargeting — when a different row lands at that position.
 */
export function rowKey(
  row: AppTableRow,
  index: number,
  columns: readonly AppTableColumn[],
): string | number {
  if (typeof row.id === "string" || typeof row.id === "number") return row.id;
  return `${index}|${columns.map((column) => cellText(row, column)).join("|")}`;
}
